<template>
  <div class="tools-container">
    <h1 class="title">All Tools</h1>
    <p class="subtitle">Everything you need in one place.</p>

    <div class="tools-grid">
      <component
        v-for="tool in tools"
        :is="tool.external ? 'a' : 'router-link'"
        :key="tool.label"
        :to="tool.external ? undefined : tool.to"
        :href="tool.external ? '#' : undefined"
        class="tool-card"
        @click="tool.external ? openProTipInBrowser($event) : null"
      >
        <div class="tool-icon">
          <img v-if="tool.avatar" :src="user.avatar || defaultAvatar" alt="Profile" class="tool-avatar" />
          <i v-else :class="tool.icon"></i>
        </div>
        <div class="tool-text">
          <h2 class="tool-label">{{ tool.label }}</h2>
          <p class="tool-description">{{ tool.description }}</p>
        </div>
      </component>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, WEB_BASE_URL } from '@/config';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

export default {
  name: 'AllTools',
  data() {
    return {
      user: {
        username: '',
        avatar: '',
      },
      defaultAvatar: 'https://6buoys-bucket.s3.amazonaws.com/avatar.png',
      isWeb: true,
      isMobile: false,
    };
  },
  computed: {
    tools() {
      return [
        {
          label: 'Pro Tips',
          description: 'Request personalized coaching tips',
          icon: 'fas fa-lightbulb',
          to: '/request-pro-tip',
          external: !this.isWeb || this.isMobile,
        },
        {
          label: 'Ski Logs',
          description: 'Track your sessions and progress',
          icon: 'fas fa-water',
          to: '/ski-logs',
        },
        {
          label: 'Compare Mode',
          description: 'Compare videos side by side',
          icon: 'fas fa-chart-line',
          to: '/compare',
        },
        {
          label: 'All Skiers',
          description: 'Browse and follow other skiers',
          icon: 'fas fa-users',
          to: '/all-skiers',
        },
        {
          label: 'Ski Schools',
          description: 'Discover ski schools and coaches',
          icon: 'fas fa-graduation-cap',
          to: '/ski-schools',
        },
        {
          label: 'Create Post',
          description: 'Share your latest ski video',
          icon: 'fas fa-plus',
          to: '/post/create',
        },
        {
          label: 'Timeline',
          description: 'Latest ski videos from the community',
          icon: 'fas fa-home',
          to: '/timeline',
        },
        {
          label: 'Contact',
          description: 'Get in touch with the 6buoys team',
          icon: 'fas fa-envelope',
          to: '/contact',
        },
        {
          label: 'Profile',
          description: 'View and manage your profile',
          avatar: true,
          to: this.user.username ? `/${this.user.username}` : '/edit-profile',
        },
      ];
    },
  },
  created() {
    const platform = Capacitor.getPlatform();
    this.isWeb = platform === 'web';
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
    this.fetchUser();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    async fetchUser() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.user = response.data;
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    },
    openProTipInBrowser(event) {
      event.preventDefault();
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const state = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('auth_state', state);

      const url = `${WEB_BASE_URL}/request-pro-tip?token=${encodeURIComponent(token)}&state=${state}`;
      window.open(url, '_blank');
    },
  },
};
</script>

<style scoped>
.tools-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
}

@media screen and (max-width: 600px) {
  .tools-container {
    padding: 20px 16px 72px 16px;
    margin: 0 auto;
    border-radius: 0;
  }
}

.title {
  text-align: center;
  color: #1da1f2;
  margin-bottom: 6px;
}

.subtitle {
  text-align: center;
  color: #8899a6;
  margin: 0 0 24px;
  font-size: 0.95em;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

@media screen and (max-width: 600px) {
  .tools-grid {
    grid-template-columns: 1fr;
  }
}

.tool-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: #15202b;
  padding: 18px;
  border-radius: 12px;
  border: 1px solid #38444d;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.tool-card:hover {
  background-color: #22303c;
  border-color: #1da1f2;
  transform: translateY(-2px);
}

.tool-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: rgba(29, 161, 242, 0.12);
  flex-shrink: 0;
}

.tool-icon i {
  font-size: 1.4em;
  color: #1da1f2;
}

.tool-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #1da1f2;
}

.tool-text {
  min-width: 0;
  text-align: left;
}

.tool-label {
  margin: 0;
  font-size: 1.1em;
  color: #ffffff;
}

.tool-description {
  margin: 4px 0 0;
  font-size: 0.85em;
  color: #8899a6;
}
</style>
